// @flow
import React, { useEffect, useState } from 'react';
// Components
import {
  validationHelpers,
  cityDropDownHelper,
  InputWithAddressAutocomplete,
  FormControlInput,
  Button,
  PhoneInput,
  formatZipcode,
  formatCNPJ,
  RadioButton,
  formatCompanyIe,
  extractAddressNumber,
  hideAddressNumber,
  formatAddressFormValues,
  getAddressPhoneError,
  Checkbox,
} from '../../../common';
// Types
import type { Address } from '../../../types';
// Analytics
import * as Analytics from '../../../common/analytics';
// Style
import './AddressForm.css';
// Hooks
import { useZipcodeSearch } from '../../customHooks';

type Props = {
  address: ?Address,
  loading: boolean,
  onSubmitAddress: (address: Address) => ?Promise<?Error>,
  onCancel: () => void,
};

type FormValues = {
  receiverName: string,
  addressLine1: string,
  addressLine2: string,
  city: string,
  zipcode: string,
  neighborhood: string,
  telephone: string,
  cnpj: string,
  ieCompany: string,
  company: string,
  isCompanyAddress: boolean,
  reference: string,
  residenceNumber: ?string,
  residenceType: string,
  residenceData: ?string,
  houseNumber: ?string,
  noNumber: boolean,
};

type FormErrors = {
  receiverNameError: string,
  addressLine1Error: string,
  addressLine2Error: string,
  cityError: string,
  zipcodeError: string,
  neighborhoodError: string,
  telephoneError: string,
  referenceError: string,
  cnpjError: string,
  ieCompanyError: string,
  companyError: string,
  residenceNumberError: string,
  residenceTypeError: string,
  residenceDataError: string,
  houseNumberError: string,
};

const initialFormValues = {
  receiverName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  zipcode: '',
  neighborhood: '',
  telephone: '',
  reference: '',
  cnpj: '',
  ieCompany: '',
  company: '',
  isCompanyAddress: false,
  residenceNumber: '',
  residenceType: '',
  residenceData: '',
  houseNumber: '',
  noNumber: false,
};

const initialErrorsValues = {
  receiverNameError: '',
  addressLine1Error: '',
  addressLine2Error: '',
  cityError: '',
  zipcodeError: '',
  neighborhoodError: '',
  telephoneError: '',
  referenceError: '',
  cnpjError: '',
  ieCompanyError: '',
  companyError: '',
  residenceNumberError: '',
  residenceTypeError: '',
  residenceDataError: '',
  houseNumberError: '',
};

const AddressForm = (props: Props) => {
  const [formValues, setFormValues] = useState<FormValues>(initialFormValues);
  const [formErrors, setFormErrors] = useState<FormErrors>(initialErrorsValues);
  const isFormValuesInvalid = Object.keys(formErrors).some(
    error => !!formErrors[error],
  );

  const { fetchAddressByZipcode } = useZipcodeSearch(
    setFormValues,
    setFormErrors,
  );

  const loadFormValues = () => {
    if (props.address?.id) {
      setFormValues(() => ({
        receiverName: !!props.address ? props.address.receiverName : '',
        addressLine1: !!props.address
          ? hideAddressNumber(props.address.addressLine1)
          : '',
        addressLine2: !!props.address ? props.address.addressLine2 : '',
        city: !!props.address
          ? props.address.city
          : cityDropDownHelper.cities[0],
        zipcode: !!props.address ? props.address.zipcode : '',
        neighborhood: !!props.address ? props.address.neighborhood : '',
        telephone: !!props.address ? props.address.telephone : '',
        reference: !!props.address ? props.address.reference : '',
        residenceNumber: !!props.address ? props.address.residenceNumber : '',
        residenceType: !!props.address ? props.address.residenceType : '',
        residenceData: !!props.address ? props.address.residenceData : '',
        cnpj: !!props.address ? props.address.cnpj : '',
        ieCompany: !!props.address ? props.address.ieCompany || '' : '',
        company: !!props.address ? props.address.company : '',
        isCompanyAddress: !!props.address ? props.address.cnpj : false,
        houseNumber: !!props.address
          ? extractAddressNumber(props.address.addressLine1)
          : '',
        noNumber:
          !!props.address &&
          extractAddressNumber(props.address.addressLine1) === 'S/N',
        id: props.address ? props.address.id : null,
      }));
    } else {
      setFormValues(preValues => ({
        ...preValues,
        city: cityDropDownHelper.cities[0],
      }));
    }
  };

  const handleFormChange = (target: string, value: string) => {
    setFormValues(prevValues => ({ ...prevValues, [target]: value }));
    setFormErrors(prevErrors => ({ ...prevErrors, [`${target}Error`]: '' }));
  };

  const handleSelectAddress = (address: Address) => {
    setFormValues(prevValues => ({ ...prevValues, ...address }));

    const addressLine1Error = validationHelpers.address.addressLine1(
      address.addressLine1,
    );

    setFormErrors(prevErrors => ({
      ...prevErrors,
      addressLine1Error,
    }));
  };

  const handleToggleIsCompanyAddress = () => {
    if (formValues.isCompanyAddress) {
      return setFormValues(prevValues => ({
        ...prevValues,
        isCompanyAddress: false,
        company: '',
        ieCompany: '',
        cnpj: '',
      }));
    }
    setFormValues(prevValues => ({ ...prevValues, isCompanyAddress: true }));
  };

  const createBlurHandler = (field, validatorFn) => () => {
    const fieldErrorKey = `${field}Error`;
    const fieldValue = formValues[field];
    const result = validatorFn(fieldValue);

    setFormErrors(prevErrors => ({
      ...prevErrors,
      [fieldErrorKey]: result,
    }));
  };

  const handleToggleResidenceType = () => {
    setFormValues(prevValues => {
      if (prevValues.residenceType === 'apartment') {
        return {
          ...prevValues,
          residenceType: 'house',
          residenceNumber: null,
          residenceData: null,
        };
      } else {
        return {
          ...prevValues,
          residenceType: 'apartment',
        };
      }
    });
  };

  const handleToggleNoNumber = () => {
    setFormValues(prevValues => {
      if (prevValues.noNumber) {
        return { ...prevValues, noNumber: false, houseNumber: '' };
      }
      return { ...prevValues, noNumber: true, houseNumber: 'S/N' };
    });
    setFormErrors(prevErrors => ({ ...prevErrors, houseNumberError: '' }));
  };

  const validateUserInput = () => {
    const receiverNameError = validationHelpers.address.name(
      formValues.receiverName,
    );

    const addressLine1Error = validationHelpers.address.addressLine1(
      formValues.addressLine1,
    );
    const addressLine2Error = validationHelpers.address.addressLine2(
      formValues.addressLine2,
    );
    const cityError = validationHelpers.address.city(formValues.city);
    const zipcodeError = validationHelpers.address.zipcode(formValues.zipcode);
    const neighborhoodError = validationHelpers.address.neighborhood(
      formValues.neighborhood,
    );
    const telephoneError = validationHelpers.address.telephone(
      formValues.telephone,
    );
    const referenceError = validationHelpers.address.pointOfReference(
      formValues.reference,
    );
    const cnpjError = formValues.isCompanyAddress
      ? validationHelpers.address.cnpj(formValues.cnpj)
      : '';
    const ieCompanyError = formValues.isCompanyAddress
      ? validationHelpers.address.ieCompany(formValues.ieCompany)
      : '';
    const companyError = formValues.isCompanyAddress
      ? validationHelpers.address.company(formValues.company)
      : '';
    const residenceNumberError = formValues.residenceNumber
      ? validationHelpers.address.residenceNumber(formValues.residenceNumber)
      : '';
    const residenceTypeError = formValues.residenceType
      ? validationHelpers.address.residenceType(formValues.residenceType)
      : '';
    const residenceDataError = formValues.residenceData
      ? validationHelpers.address.residenceData(formValues.residenceData)
      : '';
    const houseNumberError = validationHelpers.address.houseNumber(
      formValues.houseNumber,
    );

    const errors = {
      receiverNameError,
      addressLine1Error,
      addressLine2Error,
      cityError,
      zipcodeError,
      neighborhoodError,
      telephoneError,
      referenceError,
      cnpjError,
      ieCompanyError,
      companyError,
      residenceNumberError,
      residenceTypeError,
      residenceDataError,
      houseNumberError,
    };

    const isInvalid = Object.keys(errors).some(value => !!errors[value]);
    setFormErrors(errors);
    return isInvalid;
  };

  const handleSubmit = async () => {
    const isInvalid = validateUserInput();
    if (isInvalid) return;

    const error = await props.onSubmitAddress(
      formatAddressFormValues(formValues),
    );

    const telephoneError = getAddressPhoneError(error);
    if (telephoneError) {
      setFormErrors(prevErrors => ({ ...prevErrors, telephoneError }));
    }
  };

  const handleCancel = () => {
    props.onCancel();
  };

  useEffect(() => {
    loadFormValues();
    // eslint-disable-next-line
  }, [props.address?.id]);

  return (
    <form
      className="address-form"
      onSubmit={event => {
        event.preventDefault();
      }}
    >
      <section className="address-form-info">
        <h3 className="address-form-title">Informações do destinatário</h3>
        <div className="address-form-info-row">
          <FormControlInput
            label="Nome do destinatário"
            variant="rounded"
            handleChangeText={value => handleFormChange('receiverName', value)}
            handleFocus={value => handleFormChange('receiverName', value)}
            value={formValues.receiverName}
            handleBlur={createBlurHandler(
              'receiverName',
              validationHelpers.address.name,
            )}
            errorMessage={formErrors.receiverNameError}
          />
          <PhoneInput
            value={formValues.telephone}
            onChange={value => handleFormChange('telephone', value)}
            errorMessage={formErrors.telephoneError}
          />
        </div>
      </section>
      <section className="address-form-info">
        <h3 className="address-form-title">Local de entrega</h3>
        <div className="address-form-info-row-start">
          <RadioButton
            text="Residencial"
            isSelected={!formValues.isCompanyAddress}
            onSelect={() => handleToggleIsCompanyAddress()}
          />

          <RadioButton
            text="Comercial"
            isSelected={formValues.isCompanyAddress}
            onSelect={() => handleToggleIsCompanyAddress()}
          />
        </div>
        <div className="address-form-info-row-start">
          <RadioButton
            text="Apartamento"
            isSelected={formValues.residenceType === 'apartment'}
            onSelect={() => handleToggleResidenceType()}
          />
          <RadioButton
            text="Casa"
            isSelected={
              !formValues.residenceType || formValues.residenceType === 'house'
            }
            onSelect={() => handleToggleResidenceType()}
          />
        </div>
        <div className="address-form-info-row">
          <FormControlInput
            label="CEP"
            variant="rounded"
            handleChangeText={value => {
              handleFormChange('zipcode', value);
              fetchAddressByZipcode(value);
            }}
            handleFocus={value => handleFormChange('zipcode', value)}
            value={formatZipcode(formValues.zipcode)}
            maxLength={9}
            handleBlur={createBlurHandler(
              'zipcode',
              validationHelpers.address.zipcode,
            )}
            errorMessage={formErrors.zipcodeError}
          />
          <div className="address-form-select-dropdown-container">
            <label className="address-form-select-dropdown-label">Cidade</label>
            <select
              className="address-form-select-dropdown"
              onChange={e => {
                Analytics.logSelectedAddressCity(e.target.value);
                setFormValues(prevValues => ({
                  ...prevValues,
                  city: e.target.value,
                }));
                const cityError = validationHelpers.address.city(
                  e.target.value,
                );
                setFormErrors(prevErrors => ({
                  ...prevErrors,
                  cityError,
                }));
              }}
              value={formValues.city}
            >
              {cityDropDownHelper.cities.map((city, i) => {
                return (
                  <option key={i} value={city}>
                    {city}
                  </option>
                );
              })}
            </select>
            {formErrors.cityError && (
              <span className="address-form-select-dropdown-error address-form-select-dropdown-error-animation">
                {formErrors.cityError}
              </span>
            )}
          </div>
        </div>
        <div className="address-form-info-row">
          <InputWithAddressAutocomplete
            value={formValues.addressLine1}
            onChange={value => {
              setFormValues(prevValues => ({
                ...prevValues,
                addressLine1: value,
              }));
              const addressLine1Error =
                validationHelpers.address.addressLine1(value);
              setFormErrors(prevErrors => ({
                ...prevErrors,
                addressLine1Error,
              }));
            }}
            errorMessage={formErrors.addressLine1Error}
            onAddressSelect={placeDetails => handleSelectAddress(placeDetails)}
          />

          <FormControlInput
            componentLabel={
              <span className="address-form-house-number-label">
                Número{' '}
                <Checkbox
                  type="checkbox"
                  checked={!!formValues.noNumber}
                  onChange={handleToggleNoNumber}
                  label={<span>S/N</span>}
                />
              </span>
            }
            variant="rounded"
            autocomplete="off"
            handleChangeText={value =>
              handleFormChange('houseNumber', String(value).replace(/\D/g, ''))
            }
            handleFocus={value =>
              handleFormChange('houseNumber', String(value))
            }
            value={formValues.houseNumber || ''}
            disabled={formValues.noNumber}
            handleBlur={createBlurHandler(
              'houseNumber',
              validationHelpers.address.houseNumber,
            )}
            errorMessage={formErrors.houseNumberError}
          />

          <FormControlInput
            label="Bairro"
            variant="rounded"
            handleChangeText={value => handleFormChange('neighborhood', value)}
            handleFocus={value => handleFormChange('neighborhood', value)}
            value={formValues.neighborhood}
            handleBlur={createBlurHandler(
              'neighborhood',
              validationHelpers.address.neighborhood,
            )}
            errorMessage={formErrors.neighborhoodError}
          />
        </div>

        {formValues.residenceType === 'apartment' && (
          <div className="address-form-info-row">
            <FormControlInput
              label="Número do Apartamento"
              variant="rounded"
              handleChangeText={value =>
                handleFormChange('residenceNumber', value)
              }
              handleFocus={value => handleFormChange('residenceNumber', value)}
              value={formValues.residenceNumber}
              handleBlur={createBlurHandler(
                'residenceNumber',
                validationHelpers.address.residenceNumber,
              )}
              errorMessage={formErrors.residenceNumberError}
            />
            <FormControlInput
              label="Bloco"
              variant="rounded"
              handleChangeText={value =>
                handleFormChange('residenceData', value)
              }
              handleFocus={value => handleFormChange('residenceData', value)}
              value={formValues.residenceData}
              handleBlur={createBlurHandler(
                'residenceData',
                validationHelpers.address.residenceData,
              )}
              errorMessage={formErrors.residenceDataError}
            />
          </div>
        )}

        <div className="address-form-info-row">
          <FormControlInput
            label="Complemento (Opcional)"
            variant="rounded"
            handleChangeText={value => handleFormChange('addressLine2', value)}
            handleFocus={value => handleFormChange('addressLine2', value)}
            value={formValues.addressLine2}
            handleBlur={createBlurHandler(
              'addressLine2',
              validationHelpers.address.addressLine2,
            )}
            errorMessage={formErrors.addressLine2Error}
          />
          <FormControlInput
            label="Ponto de referência"
            variant="rounded"
            handleChangeText={value => handleFormChange('reference', value)}
            handleFocus={value => handleFormChange('reference', value)}
            value={formValues.reference}
            handleBlur={createBlurHandler(
              'reference',
              validationHelpers.address.pointOfReference,
            )}
            errorMessage={formErrors.referenceError}
          />
        </div>
      </section>
      {formValues.isCompanyAddress && (
        <section className="address-form-info">
          <h3 className="address-form-title">Dados da empresa</h3>
          <div className="address-form-info-row">
            <FormControlInput
              label="Nome da empresa"
              variant="rounded"
              handleChangeText={value => handleFormChange('company', value)}
              handleFocus={value => handleFormChange('company', value)}
              value={formValues.company}
              handleBlur={createBlurHandler(
                'company',
                validationHelpers.address.company,
              )}
              errorMessage={formErrors.companyError}
            />
            <FormControlInput
              label="CNPJ"
              variant="rounded"
              handleChangeText={value => handleFormChange('cnpj', value)}
              handleFocus={value => handleFormChange('cnpj', value)}
              value={formatCNPJ(formValues.cnpj)}
              handleBlur={createBlurHandler(
                'cnpj',
                validationHelpers.address.cnpj,
              )}
              errorMessage={formErrors.cnpjError}
            />
          </div>
          <div className="address-form-info-row">
            <FormControlInput
              label="Inscrição estadual"
              variant="rounded"
              handleChangeText={value => handleFormChange('ieCompany', value)}
              handleFocus={value => handleFormChange('ieCompany', value)}
              value={formatCompanyIe(formValues.ieCompany)}
              handleBlur={createBlurHandler(
                'ieCompany',
                validationHelpers.address.ieCompany,
              )}
              errorMessage={formErrors.ieCompanyError}
            />
          </div>
        </section>
      )}
      <div className="address-form-actions">
        <Button
          type="submit"
          text="Salvar endereço"
          icon=""
          loading={props.loading}
          onClick={handleSubmit}
          disabled={isFormValuesInvalid}
          buttonType="button-primary"
        />
        <Button
          text="Cancelar"
          icon=""
          onClick={handleCancel}
          buttonType="button-secondary"
        />
      </div>
    </form>
  );
};

export default AddressForm;
